import React, { useEffect, useState } from 'react';
import { CreditCard, Wallet, Banknote, ArrowLeftRight, FileMinus, Loader2 } from 'lucide-react';
import { fetchRefundCapacity, RefundCapacity, RefundDestination } from '../../lib/refunds';

/**
 * Destinations a refund can take. 'invoice' is the delivery / counter-only
 * "take it off this invoice" credit line; the rest go through the refund
 * engine. Card is shown with its cap and disabled when nothing can go back.
 */
export type PickableDestination = RefundDestination | 'invoice';

export interface RefundDestinationPickerProps {
  businessId: string;
  customerId?: string | null;
  /** Prioritises this invoice's charges when refunding to card. */
  invoiceId?: string | null;
  bookingId?: string | null;
  /** Amount owed, in cents — drives the split preview. */
  amountCents: number;
  value: PickableDestination;
  onChange: (next: PickableDestination) => void;
  /** Which options to offer, in display order. */
  destinations?: PickableDestination[];
  /** Cash / e-transfer reference (cheque #, Interac ref). */
  reference?: string;
  onReferenceChange?: (next: string) => void;
  /** Per-option hint overrides (e.g. "auto-applies to this sale first"). */
  hints?: Partial<Record<PickableDestination, string>>;
  /** Called once the card capacity is known. */
  onCapacity?: (cap: RefundCapacity) => void;
  /** Visual tone for the selected state. */
  tone?: 'emerald' | 'blue';
  compact?: boolean;
}

const fmt = (c: number) => `$${(c / 100).toFixed(2)}`;

const DEFAULT_ORDER: PickableDestination[] = ['card', 'wallet', 'cash', 'e_transfer'];

const META: Record<PickableDestination, { label: string; hint: string; Icon: React.ComponentType<{ className?: string }> }> = {
  card: { label: 'Back to card', hint: 'Stripe refund onto a card they paid with here', Icon: CreditCard },
  wallet: { label: 'Wallet credit', hint: 'store credit — applies to their next invoices automatically', Icon: Wallet },
  invoice: { label: 'Credit on this invoice', hint: 'shown as a refund line, lowers the total', Icon: FileMinus },
  cash: { label: 'Cash', hint: 'hand it back — recorded as money out', Icon: Banknote },
  e_transfer: { label: 'E-transfer', hint: 'send it — recorded as money out with a reference', Icon: ArrowLeftRight },
};

export default function RefundDestinationPicker({
  businessId,
  customerId,
  invoiceId,
  bookingId,
  amountCents,
  value,
  onChange,
  destinations = DEFAULT_ORDER,
  reference = '',
  onReferenceChange,
  hints,
  onCapacity,
  tone = 'emerald',
  compact = false,
}: RefundDestinationPickerProps) {
  const [cap, setCap] = useState<RefundCapacity | null>(null);
  const [capError, setCapError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCap(null);
    setCapError(null);
    fetchRefundCapacity({ businessId, customerId, invoiceId, bookingId })
      .then(c => {
        if (cancelled) return;
        setCap(c);
        onCapacity?.(c);
      })
      .catch(e => {
        if (cancelled) return;
        setCapError(e?.message || 'Could not check card refund capacity');
        const zero = { cardCents: 0, cardPayments: 0, stripeConnected: false, walletCents: 0 };
        setCap(zero);
        onCapacity?.(zero);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, customerId, invoiceId, bookingId]);

  // A guest has no wallet; card is capped by their refundable charges.
  const cardCap = cap?.cardCents ?? 0;
  const cardDisabledReason = !cap
    ? null
    : !cap.stripeConnected
    ? 'Stripe is not connected'
    : cardCap <= 0
    ? (customerId
        ? 'this customer has no card payments left to refund against'
        : 'add the customer — Stripe refunds go back onto a card they paid you with')
    : null;
  const walletDisabledReason = customerId ? null : 'add the customer — store credit needs an account';

  // If the current selection becomes unavailable, fall back to the first usable option.
  useEffect(() => {
    if (!cap) return;
    const usable = (d: PickableDestination) =>
      d === 'card' ? !cardDisabledReason : d === 'wallet' ? !walletDisabledReason : true;
    if (!usable(value)) {
      const next = destinations.find(usable);
      if (next && next !== value) onChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cap, cardDisabledReason, walletDisabledReason]);

  const selectedRing = tone === 'blue' ? 'border-blue-500 bg-white text-blue-800' : 'border-emerald-500 bg-white text-emerald-800';
  const idleRing = tone === 'blue' ? 'border-blue-200/60 bg-white/60 text-gray-700 hover:bg-white' : 'border-emerald-200/60 bg-white/60 text-gray-700 hover:bg-white';
  const accent = tone === 'blue' ? 'accent-blue-600' : 'accent-emerald-600';

  const cardSplit = value === 'card' && cap && amountCents > cardCap && cardCap > 0;

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      <div className="flex flex-col gap-1.5">
        {destinations.map(d => {
          const meta = META[d];
          const disabledReason = d === 'card' ? cardDisabledReason : d === 'wallet' ? walletDisabledReason : null;
          const disabled = Boolean(disabledReason);
          const hint = hints?.[d] ?? meta.hint;
          return (
            <label
              key={d}
              className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 text-[12px] font-medium transition-colors ${
                disabled ? 'cursor-not-allowed opacity-60 border-gray-200 bg-gray-50 text-gray-500' : value === d ? `cursor-pointer ${selectedRing}` : `cursor-pointer ${idleRing}`
              }`}
            >
              <input
                type="radio"
                className={`mt-0.5 ${accent}`}
                checked={value === d}
                disabled={disabled}
                onChange={() => onChange(d)}
              />
              <meta.Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="font-semibold">
                  {meta.label}
                  {d === 'card' && cap && !cardDisabledReason && (
                    <span className="font-normal text-gray-500"> — up to {fmt(cardCap)}</span>
                  )}
                  {d === 'card' && !cap && (
                    <Loader2 className="ml-1 inline h-3 w-3 animate-spin text-gray-400" />
                  )}
                </span>
                <span className="block font-normal text-gray-400">
                  {disabledReason ? disabledReason : hint}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {(value === 'cash' || value === 'e_transfer') && onReferenceChange && (
        <input
          type="text"
          value={reference}
          onChange={e => onReferenceChange(e.target.value)}
          placeholder={value === 'e_transfer' ? 'Interac reference (optional)' : 'Reference (optional)'}
          className="w-full h-9 rounded-lg border border-gray-200 px-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        />
      )}

      {cardSplit && (
        <p className="text-[11px] text-gray-600">
          {fmt(cardCap)} goes back to card; the remaining {fmt(amountCents - cardCap)} becomes wallet credit.
        </p>
      )}
      {value === 'card' && cap && !cardDisabledReason && (
        <p className="text-[11px] text-gray-400">
          Refunds land on the card that paid, usually within 5–10 business days. A charge the network declines is credited to the wallet instead.
        </p>
      )}
      {capError && <p className="text-[11px] text-red-600">{capError}</p>}
    </div>
  );
}
